/**
 * Save User to Favorites UseCase
 *
 * Handles saving/unsaving user profiles to favorites following Flutter's pattern:
 * 1. Optimistically update SecureStorage (for instant UI feedback)
 * 2. Call userService to update Firestore
 * 3. Revert SecureStorage if Firestore operation fails
 *
 * Pattern from Flutter: UsersRepository.saveUser
 * Source: lib/data/repository/users_repository.dart
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { userService } from '@/domain/services/user/userService';
import type { SavedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class SaveUserToFavoritesUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Toggle save state for a user (add/remove from favorites)
   *
   * @param userId - The Firebase ID of the user to save/unsave
   * @returns Promise<boolean> - Success status
   */
  async execute(userId: string): Promise<boolean> {
    try {
      // Get current save state from storage
      const storageKey = SECURE_STORAGE_KEYS.SAVED_USERS + userId;
      const currentSavedUser = await this.storage.getEncrypted<SavedUserStorage>(storageKey);

      // Determine new save state (toggle)
      const isCurrentlySaved = currentSavedUser !== null;
      const shouldSave = !isCurrentlySaved;

      // Step 1: Optimistically update local storage for instant UI feedback
      let newSavedUser: SavedUserStorage | null = null;

      if (shouldSave) {
        newSavedUser = {
          firebaseId: userId,
          createdDate: Date.now(),
        };
        await this.storage.setEncrypted(storageKey, newSavedUser);
      } else {
        await this.storage.remove(storageKey);
      }

      // Step 2: Call userService to update Firestore
      try {
        const success = await userService.saveUserToFavorites(userId, shouldSave);

        if (!success) {
          throw new Error('Firestore operation returned success: false');
        }
      } catch (firestoreError) {
        // Step 3: Revert storage on failure
        appLogger.error('Firestore error, reverting local storage', 'SaveUserToFavoritesUseCase.execute', { error: firestoreError as Error });

        if (currentSavedUser) {
          // Restore previous state
          await this.storage.setEncrypted(storageKey, currentSavedUser);
        } else {
          // Remove the saved user if it didn't exist before
          await this.storage.remove(storageKey);
        }

        return false;
      }

      return true;
    } catch (error) {
      appLogger.error('Error saving user to favorites', 'SaveUserToFavoritesUseCase.execute', { error: error as Error });
      return false;
    }
  }
}

// Export singleton instance
export const saveUserToFavoritesUseCase = new SaveUserToFavoritesUseCase();
